import {
  createSaveCodec,
  type GameDefinition,
  type Snapshot,
  type TransactionalSaveStore,
} from "@e308/core";
import { CONTENT_DIGEST, CONTENT_VERSION } from "./build-identity.js";
import { createConflictContent } from "./definition.js";
import type { Micro } from "./exact-numbers.js";
import type { ConflictConfig, ControllerEnvelope } from "./types.js";
import { ARMY_ALLOCATION_MIGRATION, WAVE_TIMING_MIGRATION } from "./wave-save-migration.js";

export const SAVE_SCHEMA_VERSION = 3;
export { CONTENT_DIGEST, CONTENT_VERSION } from "./build-identity.js";

export type MatchTimelineEntry =
  | {
      readonly kind: "accepted-input";
      readonly atGameMs: number;
      readonly authenticatedSeatId: string;
      readonly envelope: ControllerEnvelope;
    }
  | { readonly kind: "tick"; readonly elapsedMs: number }
  | {
      readonly kind: "controller-transition";
      readonly atGameMs: number;
      readonly seatId: string;
      readonly sessionId: string;
      readonly generation: number;
      readonly mode: "human" | "autopilot";
    }
  | { readonly kind: "pause-transition"; readonly atGameMs: number; readonly paused: boolean };

export interface MatchContainer {
  readonly format: "kilnfront-save" | "kilnfront-replay";
  readonly formatVersion: 2;
  readonly config: ConflictConfig;
  readonly timeline: readonly MatchTimelineEntry[];
  readonly checkpoint?: string;
  readonly checksum: string;
}

export function encodeSave(
  definition: GameDefinition<Micro>,
  snapshot: Snapshot<Micro>,
  config: ConflictConfig,
  timeline: readonly MatchTimelineEntry[],
  wallAnchorMs: number,
): string {
  safeTime(wallAnchorMs, "save wall anchor");
  const journal = snapshot.domainEventJournal;
  const codec = matchCodec(definition);
  const checkpoint = codec.encode(snapshot, {
    wallAnchorMs,
    entitlement: {
      policyVersion: "kilnfront-no-offline-1",
      enabled: false,
      capMs: 0,
      excess: "discard",
    },
    catchup: null,
    lastDeliveredEvent: ((journal?.nextSequence ?? 1n) - 1n).toString(),
  });
  return JSON.stringify(container("kilnfront-save", config, timeline, checkpoint));
}

export function encodeReplay(
  config: ConflictConfig,
  timeline: readonly MatchTimelineEntry[],
): string {
  return JSON.stringify(container("kilnfront-replay", config, timeline));
}

export function decodeContainer(raw: string, expected: MatchContainer["format"]): MatchContainer {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
    throw new TypeError("Invalid Kilnfront artifact");
  const source = parsed as Record<string, unknown>;
  if (source.formatVersion === 1)
    throw new TypeError(
      "This artifact requires Kilnfront ruleset 1. Keep it and open it with a compatible build; ruleset 2 changes troop routing.",
    );
  const fields = expected === "kilnfront-save" ? 6 : 5;
  if (
    Object.keys(source).length !== fields ||
    source.format !== expected ||
    source.formatVersion !== 2 ||
    !source.config ||
    typeof source.config !== "object" ||
    !Array.isArray(source.timeline) ||
    typeof source.checksum !== "string" ||
    (expected === "kilnfront-save" && typeof source.checkpoint !== "string")
  ) {
    throw new TypeError("Invalid Kilnfront artifact schema");
  }
  const { checksum, ...payload } = source;
  if (checksum !== fingerprint(payload))
    throw new TypeError("Kilnfront artifact checksum mismatch");
  validateTimeline(source.timeline);
  return source as unknown as MatchContainer;
}

export function decodeCheckpoint(definition: GameDefinition<Micro>, checkpoint: string) {
  return matchCodec(definition).decode(checkpoint);
}

export function decodeSaveArtifact(raw: string): {
  readonly config: ConflictConfig;
  readonly snapshot: Snapshot<Micro>;
  readonly timeline: readonly MatchTimelineEntry[];
} {
  const saved = decodeContainer(raw, "kilnfront-save");
  const definition = createConflictContent(saved.config).definition;
  const loaded = decodeCheckpoint(definition, saved.checkpoint as string);
  return Object.freeze({
    config: saved.config,
    snapshot: loaded.snapshot,
    timeline: saved.timeline,
  });
}

export interface StringStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export class LocalSaveStore implements TransactionalSaveStore {
  constructor(
    private readonly storage: StringStorage,
    private readonly prefix = "kilnfront:save:",
  ) {}

  async read(slot: string) {
    return this.readKey(this.key(slot));
  }

  async readBackup(slot: string) {
    return this.readKey(`${this.key(slot)}:backup`);
  }

  async compareAndSwap(slot: string, expectedRevision: string | null, value: string) {
    const key = this.key(slot);
    const current = this.readKey(key);
    if ((current?.revision ?? null) !== expectedRevision)
      return {
        ok: false as const,
        code: "conflict" as const,
        currentRevision: current?.revision ?? null,
      };
    if (current) this.storage.setItem(`${key}:backup`, JSON.stringify(current));
    const revision = (BigInt(current?.revision ?? "0") + 1n).toString();
    this.storage.setItem(key, JSON.stringify({ revision, value }));
    return { ok: true as const, revision };
  }

  private key(slot: string): string {
    if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/.test(slot)) throw new TypeError("Invalid save slot");
    return `${this.prefix}${slot}`;
  }

  private readKey(key: string): { readonly revision: string; readonly value: string } | null {
    const raw = this.storage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed.revision !== "string" || typeof parsed.value !== "string")
      throw new TypeError("Invalid stored save");
    if (!/^(?:0|[1-9][0-9]*)$/.test(parsed.revision))
      throw new TypeError("Invalid stored revision");
    return Object.freeze({ revision: parsed.revision, value: parsed.value });
  }
}

function matchCodec(definition: GameDefinition<Micro>) {
  return createSaveCodec(
    definition,
    {
      stateSchemaVersion: SAVE_SCHEMA_VERSION,
      contentVersion: CONTENT_VERSION,
      contentDigest: CONTENT_DIGEST,
    },
    { migrations: [WAVE_TIMING_MIGRATION, ARMY_ALLOCATION_MIGRATION] },
  );
}

function fingerprint(value: unknown): string {
  let result = 0xcbf29ce484222325n;
  for (const character of JSON.stringify(value)) {
    result ^= BigInt(character.codePointAt(0) as number);
    result = BigInt.asUintN(64, result * 0x100000001b3n);
  }
  return result.toString(16).padStart(16, "0");
}

function container(
  format: MatchContainer["format"],
  config: ConflictConfig,
  timeline: readonly MatchTimelineEntry[],
  checkpoint?: string,
): MatchContainer {
  const payload = {
    format,
    formatVersion: 2 as const,
    config: structuredClone(config),
    timeline: Object.freeze(structuredClone(timeline)),
    ...(checkpoint === undefined ? {} : { checkpoint }),
  };
  return Object.freeze({ ...payload, checksum: fingerprint(payload) });
}

function validateTimeline(value: readonly unknown[]): void {
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry))
      throw new TypeError("Invalid replay entry");
    const source = entry as Record<string, unknown>;
    if (source.kind === "tick") {
      if (Object.keys(source).length !== 2) throw new TypeError("Invalid replay tick");
      safeTime(source.elapsedMs, "replay elapsed time");
    } else if (source.kind === "accepted-input") {
      if (Object.keys(source).length !== 4 || typeof source.authenticatedSeatId !== "string")
        throw new TypeError("Invalid replay input");
      safeTime(source.atGameMs, "replay input time");
      if (!source.envelope || typeof source.envelope !== "object")
        throw new TypeError("Invalid replay envelope");
    } else if (source.kind === "controller-transition") {
      if (
        Object.keys(source).length !== 6 ||
        typeof source.seatId !== "string" ||
        typeof source.sessionId !== "string" ||
        !Number.isSafeInteger(source.generation) ||
        (source.generation as number) < 1 ||
        (source.mode !== "human" && source.mode !== "autopilot")
      )
        throw new TypeError("Invalid replay controller transition");
      safeTime(source.atGameMs, "replay controller transition time");
    } else if (source.kind === "pause-transition") {
      if (Object.keys(source).length !== 3 || typeof source.paused !== "boolean")
        throw new TypeError("Invalid replay pause transition");
      safeTime(source.atGameMs, "replay pause transition time");
    } else throw new TypeError("Invalid replay entry kind");
  }
}

function safeTime(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0)
    throw new TypeError(`Invalid ${label}`);
  return value as number;
}
